import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Home, ChevronRight, Save, X, Loader2, UserCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const EMPTY_FORM = {
  agent_name: '', phone: '', address: '', country: '',
  contact_person: '', email: '', remarks: '', is_active: true
};

const AddAgent = () => {
  const navigate = useNavigate();
  const { id: agentId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!agentId;

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchAgent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/agents/${agentId}`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('Failed to load agent');
        const data = await response.json();
        setFormData({
          agent_name: data.agent_name || '', phone: data.phone || '',
          address: data.address || '', country: data.country || '',
          contact_person: data.contact_person || '', email: data.email || '',
          remarks: data.remarks || '', is_active: data.is_active ?? true
        });
      } catch (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        navigate('/settings/agents');
      } finally { setLoading(false); }
    };
    fetchAgent();
  }, [isEditMode, agentId, backendUrl, getAuthHeaders, toast, navigate]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData.agent_name.trim()) { toast({ title: "Error", description: "Agent name is required", variant: "destructive" }); return; }
    try {
      setSaving(true);
      const url = isEditMode ? `${backendUrl}/api/agents/${agentId}` : `${backendUrl}/api/agents`;
      const response = await fetch(url, { method: isEditMode ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(formData) });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save agent');
      }
      toast({ title: "Success", description: isEditMode ? "Agent updated" : "Agent created" });
      navigate('/settings/agents');
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;

  return (
    <div className="space-y-6 pb-8" data-testid="add-agent-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings/agents')}>Agent Master</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">{isEditMode ? 'Edit Agent' : 'Add Agent'}</span>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-dark font-manrope">{isEditMode ? 'Edit Agent' : 'Add Agent'}</h1>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/settings/agents')} data-testid="cancel-btn"><X className="w-4 h-4 mr-2" /> Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-agent-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Agent'}
          </Button>
        </div>
      </div>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-teal-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <UserCircle className="w-5 h-5 text-teal-500" />
            <h2 className="text-lg font-semibold text-text-dark font-manrope">AGENT DETAILS</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Agent Name <span className="text-red-500">*</span></Label>
              <Input data-testid="agent-name-input" value={formData.agent_name} onChange={e => handleChange('agent_name', e.target.value)} placeholder="e.g., ABC Logistics Agent" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Phone</Label>
              <Input data-testid="phone-input" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+91 98765 43210" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Country</Label>
              <Input data-testid="country-input" value={formData.country} onChange={e => handleChange('country', e.target.value)} placeholder="e.g., India" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Contact Person</Label>
              <Input data-testid="contact-person-input" value={formData.contact_person} onChange={e => handleChange('contact_person', e.target.value)} placeholder="Contact name" className="border-gray-200" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Address</Label>
              <Textarea data-testid="address-input" value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="Full address..." className="border-gray-200 min-h-[80px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Remarks</Label>
              <Textarea data-testid="remarks-input" value={formData.remarks} onChange={e => handleChange('remarks', e.target.value)} placeholder="Optional notes..." className="border-gray-200 min-h-[80px]" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Email</Label>
              <Input data-testid="email-input" type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="agent@example.com" className="border-gray-200" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch data-testid="active-toggle" checked={formData.is_active} onCheckedChange={v => handleChange('is_active', v)} />
            <Label className="text-sm font-ibm">{formData.is_active ? 'Active' : 'Inactive'}</Label>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddAgent;
